import { Package } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { ComboWithDetails } from "@/hooks/usePublicCombos";

interface PublicComboCardProps {
  combo: ComboWithDetails;
  onClick: () => void;
}

export function PublicComboCard({ combo, onClick }: PublicComboCardProps) {
  const formatPrice = (price: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(price);

  // Get total max selections from all category slots
  const getSelectionInfo = () => {
    const categorySlots = combo.slots.filter(s => s.slot_type === 'category');
    if (categorySlots.length === 0) return null;
    
    const totalMax = categorySlots.reduce((sum, s) => sum + s.max_quantity, 0);
    return totalMax > 1 ? `Até ${totalMax} opções` : '1 opção';
  };

  // Check if any slot has variable pricing
  const hasVariablePricing = combo.slots.some(
    s => s.pricing_rule === 'highest' || s.pricing_rule === 'average'
  );

  const selectionInfo = getSelectionInfo();

  return (
    <div
      className="flex gap-3 p-3 bg-card rounded-lg border cursor-pointer hover:border-primary transition-colors"
      onClick={onClick}
    >
      {/* Image */}
      <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0 bg-muted">
        {combo.image_url ? (
          <img
            src={combo.image_url}
            alt={combo.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="h-6 w-6 text-muted-foreground" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <h3 className="font-medium text-sm line-clamp-2">{combo.name}</h3>
          {selectionInfo && (
            <Badge variant="secondary" className="flex-shrink-0 text-xs">
              <Package className="h-3 w-3 mr-1" />
              {selectionInfo}
            </Badge>
          )}
        </div>

        {combo.description && (
          <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
            {combo.description}
          </p>
        )}

        <div className="flex items-center justify-between mt-2">
          <span className="text-sm font-bold text-primary">
            {hasVariablePricing
              ? `A partir de ${formatPrice(combo.price)}`
              : formatPrice(combo.price)}
          </span>
        </div>
      </div>
    </div>
  );
}
